import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query } from 'firebase/firestore';
import { db, onSnapshot } from '@/lib/firebase';
import { Cursor } from '@/types';
import { logger } from '@/lib/logger';

const CURSOR_STALE_TIMEOUT = 5000; // 5 seconds

export const useBoardCursors = (
  boardId: string | null,
  currentUserId: string | undefined
): Map<string, Cursor> => {
  const [cursors, setCursors] = useState<Map<string, Cursor>>(new Map());
  const cursorsRef = useRef<Map<string, Cursor>>(new Map());

  const updateCursors = useCallback(() => {
    // Filter out current user's cursor and stale cursors
    const now = Date.now();
    const filteredCursors = new Map<string, Cursor>();

    for (const [userId, cursor] of cursorsRef.current.entries()) {
      // Skip current user's cursor
      if (userId === currentUserId) {
        continue;
      }

      // Filter out stale cursors (>5 seconds old)
      const cursorAge = now - cursor.lastUpdated.toMillis();
      if (cursorAge > CURSOR_STALE_TIMEOUT) {
        continue;
      }

      filteredCursors.set(userId, cursor);
    }

    setCursors(filteredCursors);
  }, [currentUserId]);

  useEffect(() => {
    if (!boardId) {
      setCursors(new Map());
      cursorsRef.current = new Map();
      return;
    }

    const cursorsRef_collection = collection(db, 'boards', boardId, 'cursors');
    const q = query(cursorsRef_collection);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newCursors = new Map<string, Cursor>();

        snapshot.forEach((doc) => {
          const cursorData = doc.data() as Cursor;
          newCursors.set(doc.id, cursorData);
        });

        cursorsRef.current = newCursors;
        updateCursors();
      },
      (error) => {
        logger('cursor').error('Error listening to cursors:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [boardId, updateCursors]);

  return cursors;
};


